import React, { memo, useCallback, useContext, useState } from 'react'
import clsx from 'clsx'
import { FormProvider, useForm } from 'react-hook-form'
import { Box, Typography } from '@mui/material'
import { Button } from 'src/components/atoms'
import Modal from 'src/components/molecules/Modal'
import { ToastTriggerContext } from 'src/context/toast.context'
import {
  MemberPostRequest,
  RoleEnum,
  Store as EntityStore,
} from 'src/slices/services/api'
import listStyles from 'src/styles/list-styles.module.scss'
import MembersCreateTable, {
  MembersFormType,
} from '../../components/CreateTable'
import { validateMembersData } from '../../function'
import { useMembersSubmitDisabled } from '../../useMembersSubmitDisabled'
import memberUpdateStyle from '../Update/styles.module.scss' // MemberCreateとMemberUpdateのstyleがほぼ同じなので、Updateのものを流用している。

export type TemplatesMembersCreateProps = {
  onClickConfirmClose: () => void
  onCreateMembers: (members: MemberPostRequest[]) => void
  stores: EntityStore[]
  selfRole: RoleEnum
  orgCode: string
}

const TemplatesMembersCreate: React.StyledFC<TemplatesMembersCreateProps> =
  memo(
    ({
      onClickConfirmClose,
      onCreateMembers,
      stores,
      selfRole,
      orgCode,
    }: TemplatesMembersCreateProps) => {
      const toastContext = useContext(ToastTriggerContext)
      const [showCancelModal, setShowCancelModal] = useState(false)
      const [duplicationEmails, setDuplicationEmails] = useState<string[]>([])
      const [duplicationMessage, setDuplicationMessage] = useState<string>('')
      const formMethods = useForm<MembersFormType>({
        defaultValues: { members: [{ email: '', name: '' }] },
        mode: 'onBlur',
      })
      const isSubmitDisabled = useMembersSubmitDisabled(formMethods)

      const onClickClosePage = () => {
        setShowCancelModal(true)
      }
      const onClickCancelClose = () => {
        setShowCancelModal(false)
      }

      const getSelfDuplication = useCallback(
        (members: MembersFormType['members']) => {
          const emails = members
            .filter((member) => member)
            .map((member) => member.email)
          return emails.filter(
            (email, index) => emails.indexOf(email) !== index
          )
        },
        []
      )

      const handleSubmit = () => {
        const formData = formMethods.getValues()
        const validation = validateMembersData(formData.members)

        if (!validation.isValid && validation.message) {
          toastContext.sendToast({
            variant: 'error',
            title: validation.message,
          })
          return
        }

        const duplication = getSelfDuplication(formData.members)
        if (duplication.length > 0) {
          setDuplicationEmails(duplication)
          setDuplicationMessage('登録メールが重複しています。削除してください')
          return
        }
        const members: MemberPostRequest[] = []
        formData.members.forEach((member) => {
          if (!member) {
            return
          }
          members.push({
            name: member.name,
            email: member.email,
            role: member.role,
            hasAllStoresPermission: member.assignedStores.allChecked,
            assignedStoreIds: member.assignedStores.checkListValues,
          })
        })
        onCreateMembers(members)
      }

      return (
        <>
          <FormProvider {...formMethods}>
            <form>
              <Box className={listStyles.container}>
                <Typography className={memberUpdateStyle.title} variant="h1">
                  ユーザー新規追加
                </Typography>
                <Box
                  className={clsx(
                    memberUpdateStyle.mainContent,
                    memberUpdateStyle.tableContainer
                  )}
                >
                  <MembersCreateTable
                    orgCode={orgCode}
                    stores={stores}
                    selfRole={selfRole}
                  />
                </Box>
                <Box className={memberUpdateStyle.bottom}>
                  <Button
                    title="もどる"
                    size="medium"
                    color="secondary"
                    onClick={onClickClosePage}
                  />
                  <Button
                    title="追加する"
                    size="medium"
                    color="primary"
                    type="button"
                    disabled={isSubmitDisabled}
                    onClick={formMethods.handleSubmit(handleSubmit)}
                  />
                </Box>
              </Box>
            </form>
          </FormProvider>
          <Modal
            open={showCancelModal}
            title={`今中断するとこれまでの設定内容は\n失われてしまいますがよろしいですか？`}
            agreeButtonTitle="このまま中断する"
            disagreeButtonTitle="設定に戻る"
            onClickAgree={onClickConfirmClose}
            onClickDisagree={onClickCancelClose}
          />
          <Modal
            open={duplicationEmails && duplicationEmails.length > 0}
            disableBackdropClick={false}
            title={duplicationMessage}
            agreeButtonTitle="ok"
            onClose={() => setDuplicationEmails([])}
            onClickAgree={() => setDuplicationEmails([])}
          >
            <Box>
              {duplicationEmails.map((email) => (
                <Typography
                  variant="body1"
                  className={memberUpdateStyle.duplicateList}
                  key={email}
                >
                  {email}
                </Typography>
              ))}
            </Box>
          </Modal>
        </>
      )
    }
  )

TemplatesMembersCreate.displayName = 'TemplatesMembersCreate'
export default TemplatesMembersCreate
